import { useCallback, useEffect, useMemo, useState } from 'react';
import { useInfiniteScroll } from './useInfiniteScroll';
import type { UseQueryResult } from '@tanstack/react-query';
import type { PaginatedResponse } from '~/types';

/**
 * Drives an infinite-scroll list backed by a paginated React Query hook.
 * Accumulates pages into a single `data` array and automatically fetches the
 * next page when the sentinel element scrolls into view.
 *
 * @param fetchHook        - A React Query-backed hook with the signature
 *                           `(page, pageSize, filters?) => UseQueryResult`.
 *                           Must accept `page` (0-based) and `pageSize` as its
 *                           first two positional arguments.
 *
 * @param select           - Extracts `{ content, paging }` from the raw hook
 *                           result. Keeps this hook agnostic about each
 *                           endpoint's response shape.
 *
 * @param options.pageSize - Number of items per page (default: 10).
 *
 * @param options.filters  - Optional search/sort/filter params forwarded to
 *                           `fetchHook` as its third argument. Changing these
 *                           resets the list back to page 0 automatically.
 *
 * @returns
 *   - `data`          — Accumulated list of all fetched items.
 *   - `sentinelRef`   — Attach to a `<div>` at the bottom of the list to
 *                       trigger the next page load on scroll.
 *   - `isLoadingMore` — `true` while a subsequent page is being fetched.
 *   - `isLoading`     — `true` during any loading state (including page 0).
 *   - `hasMoreData`   — `true` while the server has more pages to return.
 *   - `error`         — Query error, if any.
 *   - `pagination`    — Raw paging metadata from the last server response
 *                       (`{ number, size, totalElements, totalPages }`).
 *   - `reset`         — Clears all accumulated items and returns to page 0.
 *                       Call this after a mutation that invalidates the list.
 *
 * @example — no filters
 *   const { data, sentinelRef, isLoadingMore } = usePaginatedQuery({
 *     fetchHook: useGetBuyerInventories,
 *     select: (data) => data?.buyerInventory,
 *   });
 *
 * @example — with filters and custom page size
 *   const { data, sentinelRef, pagination } = usePaginatedQuery({
 *     fetchHook: useGetProducts,
 *     select: (data) => data?.products,
 *     options: { pageSize: 20, filters: { search, sortBy } },
 *   });
 */

interface UsePaginatedQueryOptions<
  TRaw,
  TItem extends { id: string },
  TFilters,
> {
  fetchHook: (
    page: number,
    pageSize: number,
    filters?: TFilters
  ) => UseQueryResult<TRaw, Error>;
  select: (data: TRaw | undefined) => PaginatedResponse<TItem> | undefined;
  options?: {
    pageSize?: number;
    filters?: TFilters;
  };
}

export type FilterCriteria = {
  operator: string;
  value: string | number | boolean;
  field: string;
};

export function usePaginatedQuery<
  TRaw,
  TItem extends { id: string },
  TFilters = FilterCriteria[],
>({
  fetchHook,
  select,
  options,
}: UsePaginatedQueryOptions<TRaw, TItem, TFilters>) {
  const pageSize = options?.pageSize ?? 10;
  const filters = options?.filters;

  const [page, setPage] = useState(0);
  const [allItems, setAllItems] = useState<TItem[]>([]);

  const filtersKey = useMemo(() => JSON.stringify(filters ?? null), [filters]);

  useEffect(() => {
    function resetPage() {
      setPage(0);
      setAllItems([]);
    }
    resetPage();
  }, [filtersKey]);

  const rawResult = fetchHook(page, pageSize, filters);
  const paginatedData = select(rawResult.data);

  const firstPageLoaded =
    allItems.length > 0 || paginatedData?.paging.totalElements === 0;

  let hasMoreData: boolean | null = null;
  hasMoreData = rawResult.isSuccess
    ? allItems.length < (paginatedData?.paging.totalElements || 0)
    : null;

  const onLoadMore = useCallback(() => setPage((p) => p + 1), []);

  const {
    sentinelRef,
    isLoadingMore,
    reset: resetIsLoadingMore,
  } = useInfiniteScroll({
    hasMore: hasMoreData,
    isLoading: rawResult.isLoading || !firstPageLoaded,
    onLoadMore,
  });

  const reset = useCallback(() => {
    setPage(0);
    setAllItems([]);
    resetIsLoadingMore();
  }, [resetIsLoadingMore]);

  useEffect(() => {
    const incoming = paginatedData?.content;
    if (incoming === undefined || incoming === null) {
      resetIsLoadingMore();
      return;
    }
    const nonNullIncoming = incoming.filter((item) => item !== null);

    const responsePage = paginatedData?.paging.number ?? page;
    const clearItems = () => setAllItems([]);
    if (nonNullIncoming.length === 0) {
      if (responsePage === 0) clearItems();
      resetIsLoadingMore();
      return;
    }

    const appendItems = () =>
      setAllItems((prev) => {
        const incomingById = new Map(
          nonNullIncoming.map((item) => [item.id, item])
        );

        if (responsePage === 0) {
          const incomingIds = new Set(incomingById.keys());
          const preserved = prev.filter((item) => !incomingIds.has(item.id));
          return [...nonNullIncoming, ...preserved];
        }
        const updated = prev.map((item) => incomingById.get(item.id) ?? item);
        const prevIds = new Set(prev.map((item) => item.id));
        const brandNew = nonNullIncoming.filter(
          (item) => !prevIds.has(item.id)
        );
        return [...updated, ...brandNew];
      });

    appendItems();
    resetIsLoadingMore();
  }, [
    paginatedData?.content,
    paginatedData?.paging.number,
    page,
    resetIsLoadingMore,
  ]);

  const updateItem = useCallback(
    (id: string, updater: (item: TItem) => TItem) => {
      setAllItems((prev) =>
        prev.map((item) => (item.id === id ? updater(item) : item))
      );
    },
    []
  );

  return {
    data: allItems,
    sentinelRef, //Attach this to a sentinel `<div>` at the bottom of your list.
    isLoadingMore, //True while a subsequent page is being fetched
    isLoading: rawResult.isLoading, //True during the initial page load when page is 0.
    hasMoreData, //False once all pages have been loaded. null before the first page returns.
    error: rawResult.error,
    pagination: paginatedData?.paging,
    reset,
    updateItem,
  };
}
